import { useEffect, useRef, useState } from "react";

interface Step {
  number: string;
  title: string;
  subtitle: string;
  features: string[];
}

const steps: Step[] = [
  {
    number: "КРОК 1",
    title: "Публікуєте вакансію",
    subtitle: "Вона потрапляє в потрібні міста й канали:",
    features: [
      "мультипостинг на Work.ua, Robota.ua та інші портали",
      "георозширення під різні міста, райони й торгові точки",
      "форми відгуку під різні ролі",
      "QR-коди на касах чи флаєрах ведуть кандидата у єдину форму",
    ],
  },
  {
    number: "КРОК 2",
    title: "Збираєте всіх кандидатів в одну систему",
    subtitle: "Відповідаєте першими — наймаєте першими:",
    features: [
      "відгуки з порталів, сайту, форм і месенджерів потрапляють в одну ATS",
      "після відгуку запускаються SMS-сповіщення, AI-агенти або Telegram/Viber-боти",
      "бот закриває базові питання, допомагає обрати посаду або зручну локацію",
      "кандидат отримує перший контакт, поки інші ще розбирають заявки",
    ],
  },
  {
    number: "КРОК 3",
    title: "Оцінка і комунікація",
    subtitle: "Рекрутеру не треба перемикатися по 20 вкладках:",
    features: [
      "дзвінок кандидату в 1 клік з системи",
      "підсумки, статуси, коментарі й дзвінки зберігаються автоматично",
      "голосові боти для первинного відсіву",
      "AI-скринінг, оцінка і soft skills для офісних ролей",
    ],
  },
  {
    number: "КРОК 4",
    title: "Пребординг і вихід",
    subtitle: "Кандидат стає співробітником автоматично:",
    features: [
      "форма пребордингу для документів, даних і сканів",
      "дані передаються в інтегровані системи кадрового обліку, ERP та інші внутрішні системи",
      "рекрутинг, пребординг і вихід на роботу залишаються в одному контурі",
    ],
  },
  {
    number: "КРОК 5",
    title: "Аналітика по мережі",
    subtitle: "Ви бачите вузькі місця й керуєте наймом по всіх локаціях:",
    features: [
      "конверсії джерел, причини відмов, навантаження рекрутерів",
      "повноцінна оргструктура — інтегрується з ERP і системами обліку",
      "дані передаються через API в Looker Studio, Power BI або інші BI-системи",
    ],
  },
];

const BG_IMAGE =
  "https://storage.readdy-site.link/project_files/7cf81fbf-fb7d-4618-bb94-f6f654527b2e/708f4019-3c94-409a-aaba-d26ace29c0e4_Frame-1000000992.png?v=fa8edaeb9448f755324d199b135154fe";

function StepCard({
  step,
  isLeft,
  isLast,
  delay,
  isVisible,
}: {
  step: Step;
  isLeft: boolean;
  isLast: boolean;
  delay: number;
  isVisible: boolean;
}) {
  return (
    <div
      className={`relative flex items-start transition-all duration-700 ease-out ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      } ${isLeft ? "lg:flex-row" : "lg:flex-row-reverse"}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Card side */}
      <div className={`flex-1 lg:w-[calc(50%-40px)] ${isLeft ? "lg:pr-0" : "lg:pl-0"}`}>
        <div className={`${isLeft ? "lg:mr-12" : "lg:ml-12"}`}>
          <div className="bg-white/90 backdrop-blur-sm border border-[#e5e5ea] rounded-[24px] p-7 md:p-8 relative overflow-hidden min-h-[280px] md:min-h-[320px] transition-all duration-300 hover:bg-white hover:shadow-lg group">
            {/* Big faded number */}
            <span className="absolute top-5 right-5 md:top-6 md:right-6 text-[72px] md:text-[88px] font-bold leading-none text-[#1d1d1f]/5 select-none">
              {step.number}
            </span>

            {/* Content */}
            <div className="relative z-10 pt-20 md:pt-24">
              {/* Title with yellow-green animated gradient */}
              <h3 className="text-[22px] md:text-[26px] font-semibold gradient-text-shimmer-yellow-green leading-tight mb-3 md:mb-4">
                {step.title}
              </h3>

              {/* Description */}
              <p className="text-[17px] md:text-[19px] text-[#6e6e73] font-medium leading-normal mb-4 md:mb-5">
                {step.subtitle}
              </p>

              {/* Features list */}
              <ul className="space-y-2">
                {step.features.map((feature, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2.5"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#facc15] flex-shrink-0 mt-2" />
                    <span className="text-[15px] md:text-[16px] text-[#6e6e73] leading-relaxed">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Empty side for balance */}
      <div className="hidden lg:block flex-1" />
    </div>
  );
}

export default function SolutionSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [dotTop, setDotTop] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current || !timelineRef.current) return;
      const sectionRect = sectionRef.current.getBoundingClientRect();
      const timelineRect = timelineRef.current.getBoundingClientRect();
      const sectionTop = sectionRect.top;
      const sectionHeight = sectionRect.height;
      const viewportH = window.innerHeight;
      const dotH = 36;

      const progress = Math.max(
        0,
        Math.min(
          1,
          (-sectionTop + viewportH * 0.35) / (sectionHeight - viewportH * 0.3)
        )
      );

      const maxTravel = Math.max(0, timelineRect.height - dotH);
      setDotTop(progress * maxTravel);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section
      id="solution"
      ref={sectionRef}
      className="relative py-20 md:py-32 overflow-hidden"
    >
      {/* Fixed background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${BG_IMAGE})`,
          backgroundAttachment: "fixed",
        }}
      />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a12]/85 via-[#0a0a12]/70 to-[#0a0a12]/85" />

      <div className="relative z-10 max-w-[1200px] mx-auto px-4 md:px-8">
        {/* Heading */}
        <div
          className={`text-center mb-20 md:mb-28 transition-all duration-1000 ease-out ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}
        >
          <h2 className="text-[32px] sm:text-[44px] lg:text-[56px] font-bold leading-[1.05] tracking-tight text-white mb-6">
            HURMA зв&apos;язує{" "}
            <span className="gradient-text-shimmer-yellow-green">
              весь шлях кандидата
            </span>
          </h2>
          <p className="text-[17px] md:text-[20px] text-white/80 leading-relaxed max-w-[600px] mx-auto">
            Від публікації вакансії до виходу на роботу — все в одній системі
          </p>
        </div>

        {/* Timeline container */}
        <div ref={timelineRef} className="relative">
          {/* Central line - desktop */}
          <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-[#facc15]/40 via-[#facc15]/25 to-[#facc15]/10 -translate-x-1/2 rounded-full" />

          {/* Floating dot — desktop */}
          <div
            className="hidden lg:block absolute left-1/2 -translate-x-1/2 pointer-events-none z-20"
            style={{ top: dotTop }}
          >
            <svg width="36" height="36" viewBox="0 0 73 73" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask0_2208_18932" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="73" height="73">
                <path fillRule="evenodd" clipRule="evenodd" d="M0 0H73V73H0V0Z" fill="white" />
              </mask>
              <g mask="url(#mask0_2208_18932)">
                <path fillRule="evenodd" clipRule="evenodd" d="M36.3886 0.00068868C45.767 -0.0282359 54.1985 4.49088 61.3546 11.6463C68.4818 18.7727 73.0002 27.1588 73.0002 36.4993V36.3885C73.0291 45.7669 68.5107 54.1984 61.3546 61.3545C54.2275 68.4816 45.8428 73.0001 36.5015 73.0001H36.6117C27.234 73.029 18.8018 68.5099 11.6457 61.3545C4.51994 54.2273 0.000131551 45.842 0.000131551 36.5014V36.6123C-0.0281043 27.2338 4.49101 18.8017 11.6457 11.6463C18.7729 4.51981 27.1582 0 36.4988 0L36.3886 0.00068868Z" fill="#F96B25" />
              </g>
              <path fillRule="evenodd" clipRule="evenodd" d="M46.722 20.4913C40.0061 20.3478 37.0899 23.865 36.3789 27.2832C35.3269 25.3201 33.9048 22.9721 31.8115 21.794C30.68 21.1578 28.4937 20.622 25.4501 20.3727C22.4349 20.126 20.5977 18.7734 20.5977 18.7734C20.5977 18.7734 20.5707 22.7564 20.4952 26.2865C20.3517 33.0023 23.8684 35.9184 27.2873 36.6287C25.3241 37.6821 22.9761 39.1034 21.798 41.1973C21.1611 42.3275 20.626 44.5145 20.3766 47.5586C20.13 50.5732 18.7773 52.4096 18.7773 52.4096C18.7773 52.4096 22.7604 52.4373 26.2905 52.5127C33.0064 52.657 35.9226 49.1397 36.633 45.7215C37.6857 47.6847 39.107 50.0327 41.201 51.21C42.3319 51.8462 44.5189 52.3813 47.5631 52.6307C50.5777 52.878 52.4142 54.2306 52.4142 54.2306C52.4142 54.2306 52.4418 50.2476 52.5173 46.7176C52.6608 40.0018 49.1442 37.0863 45.7252 36.3747C47.6891 35.3226 50.0372 33.9006 51.2146 31.8074C51.8508 30.6758 52.3859 28.4896 52.6352 25.4461C52.8826 22.4309 54.2345 20.5937 54.2345 20.5937C54.2345 20.5937 50.2522 20.5668 46.722 20.4913Z" fill="#293041" />
            </svg>
          </div>

          {/* Floating dot — mobile */}
          <div
            className="lg:hidden absolute left-6 -translate-x-1/2 pointer-events-none z-20"
            style={{ top: dotTop }}
          >
            <svg width="28" height="28" viewBox="0 0 73 73" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="maskMobileFloat_2208_18932" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="73" height="73">
                <path fillRule="evenodd" clipRule="evenodd" d="M0 0H73V73H0V0Z" fill="white" />
              </mask>
              <g mask="url(#maskMobileFloat_2208_18932)">
                <path fillRule="evenodd" clipRule="evenodd" d="M36.3886 0.00068868C45.767 -0.0282359 54.1985 4.49088 61.3546 11.6463C68.4818 18.7727 73.0002 27.1588 73.0002 36.4993V36.3885C73.0291 45.7669 68.5107 54.1984 61.3546 61.3545C54.2275 68.4816 45.8428 73.0001 36.5015 73.0001H36.6117C27.234 73.029 18.8018 68.5099 11.6457 61.3545C4.51994 54.2273 0.000131551 45.842 0.000131551 36.5014V36.6123C-0.0281043 27.2338 4.49101 18.8017 11.6457 11.6463C18.7729 4.51981 27.1582 0 36.4988 0L36.3886 0.00068868Z" fill="#F96B25" />
              </g>
              <path fillRule="evenodd" clipRule="evenodd" d="M46.722 20.4913C40.0061 20.3478 37.0899 23.865 36.3789 27.2832C35.3269 25.3201 33.9048 22.9721 31.8115 21.794C30.68 21.1578 28.4937 20.622 25.4501 20.3727C22.4349 20.126 20.5977 18.7734 20.5977 18.7734C20.5977 18.7734 20.5707 22.7564 20.4952 26.2865C20.3517 33.0023 23.8684 35.9184 27.2873 36.6287C25.3241 37.6821 22.9761 39.1034 21.798 41.1973C21.1611 42.3275 20.626 44.5145 20.3766 47.5586C20.13 50.5732 18.7773 52.4096 18.7773 52.4096C18.7773 52.4096 22.7604 52.4373 26.2905 52.5127C33.0064 52.657 35.9226 49.1397 36.633 45.7215C37.6857 47.6847 39.107 50.0327 41.201 51.21C42.3319 51.8462 44.5189 52.3813 47.5631 52.6307C50.5777 52.878 52.4142 54.2306 52.4142 54.2306C52.4142 54.2306 52.4418 50.2476 52.5173 46.7176C52.6608 40.0018 49.1442 37.0863 45.7252 36.3747C47.6891 35.3226 50.0372 33.9006 51.2146 31.8074C51.8508 30.6758 52.3859 28.4896 52.6352 25.4461C52.8826 22.4309 54.2345 20.5937 54.2345 20.5937C54.2345 20.5937 50.2522 20.5668 46.722 20.4913Z" fill="#293041" />
            </svg>
          </div>

          {/* Mobile line */}
          <div className="lg:hidden absolute left-6 top-0 bottom-0 w-1 bg-gradient-to-b from-[#facc15]/40 via-[#facc15]/25 to-[#facc15]/10 rounded-full" />

          {/* Steps */}
          <div className="space-y-8 md:space-y-12">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                {/* Mobile card */}
                <div className="lg:hidden ml-14">
                  <div
                    className={`bg-white/90 backdrop-blur-sm border border-[#e5e5ea] rounded-[24px] p-7 relative overflow-hidden min-h-[260px] transition-all duration-700 ease-out ${
                      isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                    }`}
                    style={{ transitionDelay: `${200 + index * 150}ms` }}
                  >
                    {/* Big faded number */}
                    <span className="absolute top-5 right-5 text-[64px] font-bold leading-none text-[#1d1d1f]/5 select-none">
                      {step.number}
                    </span>

                    {/* Content */}
                    <div className="relative z-10 pt-16">
                      {/* Title with yellow-green animated gradient */}
                      <h3 className="text-[20px] font-semibold gradient-text-shimmer-yellow-green leading-tight mb-3">
                        {step.title}
                      </h3>

                      {/* Description */}
                      <p className="text-[15px] text-[#6e6e73] font-medium leading-normal mb-4">
                        {step.subtitle}
                      </p>

                      {/* Features list */}
                      <ul className="space-y-2">
                        {step.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#facc15] flex-shrink-0 mt-1.5" />
                            <span className="text-[14px] text-[#6e6e73] leading-relaxed">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Desktop zigzag */}
                <div className="hidden lg:block">
                  <StepCard
                    step={step}
                    isLeft={index % 2 === 0}
                    isLast={index === steps.length - 1}
                    delay={200 + index * 150}
                    isVisible={isVisible}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}